import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, KeyRound, Loader2 } from 'lucide-react'
import {
  useAcquisitionSummaryQuery,
  useConvertAcquisitionModeMutation,
  type ConvertAcquisitionModeResult,
  type ConvertAcquisitionSkip,
  type TagRecord
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  ACQUISITION_HISTORY_WARNING,
  acquisitionDestinationOptions,
  conversionSourceUrls,
  normalizeAcquisitionUrl,
  targetModeForSourceMode,
  validateConversionSourceUrls
} from './acquisition-source'

interface ChangeSourceDialogProps {
  open: boolean
  tags: TagRecord[]
  onOpenChange: (open: boolean) => void
  onApplied?: (result: ConvertAcquisitionModeResult) => void
}

const SKIP_REASON_LABELS: Record<string, string> = {
  not_in_requested_pair: 'Tag is no longer registered to the reviewed source and mode.',
  graph_only: 'No history registration exists for this graph tag.',
  not_ignition_provider_path: 'The tag does not use a convertible Ignition provider path.',
  no_synthesizable_opc_node_id: 'An OPC node ID could not be derived from the tag path.',
  no_canonical_ignition_path: 'A canonical Ignition path could not be derived for WebDev polling.'
}

function skipReason(detail: ConvertAcquisitionSkip): string {
  return SKIP_REASON_LABELS[detail.reason] ?? detail.reason
}

function resultItemIdentity(item: { tagId: string; tagPath: string; nodeId?: string }): string {
  return item.tagPath || item.tagId || item.nodeId || 'Unknown tag'
}

export default function ChangeSourceDialog({
  open,
  tags,
  onOpenChange,
  onApplied
}: ChangeSourceDialogProps) {
  const summaryQuery = useAcquisitionSummaryQuery()
  const [convertMode, convertState] = useConvertAcquisitionModeMutation()
  const [destinationUrl, setDestinationUrl] = useState('')
  const [opcUsername, setOpcUsername] = useState('')
  const [opcPassword, setOpcPassword] = useState('')
  const [replaceSavedCredentials, setReplaceSavedCredentials] = useState(false)
  const [preview, setPreview] = useState<ConvertAcquisitionModeResult | null>(null)
  const [result, setResult] = useState<ConvertAcquisitionModeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const selectionKey = tags
    .map((tag) => `${tag.id}\u0000${tag.sourceUrl}\u0000${tag.scrapeMode}`)
    .join('\u0001')
  const tagIds = [...new Set(tags.map((tag) => tag.id))]
  const sourceUrl = tags[0]?.sourceUrl ?? ''
  const sourceMode = tags[0]?.scrapeMode
  const targetMode = targetModeForSourceMode(sourceMode)
  const destinationOptions = useMemo(
    () =>
      targetMode ? acquisitionDestinationOptions(summaryQuery.data?.sourceUrls, targetMode) : [],
    [summaryQuery.data?.sourceUrls, targetMode]
  )
  const savedOPCEndpoint = useMemo(() => {
    if (targetMode !== 'subscribe') return undefined
    const normalizedDestination = normalizeAcquisitionUrl(destinationUrl)
    if (!normalizedDestination) return undefined
    return summaryQuery.data?.opcEndpoints.find(
      (endpoint) => normalizeAcquisitionUrl(endpoint.endpointUrl) === normalizedDestination
    )
  }, [destinationUrl, summaryQuery.data?.opcEndpoints, targetMode])
  const savedCredentialsAvailable =
    savedOPCEndpoint?.hasSavedCredentials === true && !savedOPCEndpoint.anonymousAccess
  const reuseSavedCredentials = savedCredentialsAvailable && !replaceSavedCredentials

  useEffect(() => {
    if (!open) return
    setDestinationUrl('')
    setOpcUsername('')
    setOpcPassword('')
    setReplaceSavedCredentials(false)
    setPreview(null)
    setResult(null)
    setError(null)
  }, [open, selectionKey])

  useEffect(() => {
    if (!open || destinationOptions.length !== 1) return
    setDestinationUrl((current) => current || destinationOptions[0])
  }, [destinationOptions, open, selectionKey])

  const resetReview = () => {
    setPreview(null)
    setResult(null)
    setError(null)
  }

  const request = async (previewOnly: boolean) => {
    if (!targetMode) {
      setError('Change source supports WebDev poll and OPC subscribe registrations.')
      return
    }
    const validationError = validateConversionSourceUrls(
      targetMode,
      sourceUrl,
      destinationUrl,
      opcUsername,
      opcPassword
    )
    if (validationError) {
      setError(validationError)
      return
    }

    const { httpGatewayUrl, opcEndpointUrl } = conversionSourceUrls(
      targetMode,
      sourceUrl,
      destinationUrl
    )
    const username = opcUsername.trim()
    setError(null)
    try {
      const response = await convertMode({
        targetMode,
        httpGatewayUrl,
        opcEndpointUrl,
        tagIds,
        preview: previewOnly,
        connection:
          targetMode === 'subscribe' && username
            ? {
                endpointUrl: opcEndpointUrl,
                anonymousAccess: false,
                username,
                password: opcPassword
              }
            : undefined
      })
      if (previewOnly) {
        setPreview(response)
        setResult(null)
      } else {
        setResult(response)
        setOpcPassword('')
        onApplied?.(response)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    }
  }

  const reviewed = result ?? preview
  const toSubscribe = targetMode === 'subscribe'
  const busy = convertState.isLoading

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy) onOpenChange(nextOpen)
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-hidden p-0 sm:max-w-160"
        data-testid="change-source-dialog"
      >
        <DialogHeader className="border-b border-line px-5 py-4">
          <DialogTitle className="text-base">Change source</DialogTitle>
          <DialogDescription className="text-12">
            Review {tagIds.length.toLocaleString()} registered tag
            {tagIds.length === 1 ? '' : 's'} before switching from{' '}
            {sourceMode === 'subscribe' ? 'OPC subscribe' : 'WebDev poll'} to{' '}
            {toSubscribe ? 'OPC subscribe' : 'WebDev poll'}.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-3 overflow-y-auto px-5 py-1">
          <div
            className="flex items-start gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg p-3 text-12 font-medium leading-relaxed text-st-warn"
            data-testid="change-source-history-warning"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{ACQUISITION_HISTORY_WARNING}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0 space-y-1.5">
              <Label className="text-11">Current source</Label>
              <div
                className="truncate rounded-md border border-line bg-bg-sunk px-3 py-2 font-mono text-11 text-ink-2"
                title={sourceUrl}
                data-testid="change-source-current"
              >
                {sourceUrl}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-11">Target transport</Label>
              <div className="rounded-md border border-line bg-bg-sunk px-3 py-2 text-11 font-medium text-ink-2">
                {toSubscribe ? 'OPC UA subscribe' : 'WebDev poll'}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="change-source-destination" className="text-11">
              {toSubscribe ? 'Destination OPC endpoint' : 'Destination HTTP gateway'}
            </Label>
            <Input
              id="change-source-destination"
              value={destinationUrl}
              onChange={(event) => {
                setDestinationUrl(event.target.value)
                setOpcUsername('')
                setOpcPassword('')
                setReplaceSavedCredentials(false)
                resetReview()
              }}
              placeholder={toSubscribe ? 'opc.tcp://gateway:62541' : 'https://gateway:8088'}
              className="h-8 font-mono text-11"
              disabled={busy}
              data-testid="change-source-destination"
            />
            {destinationOptions.length > 0 ? (
              <div
                className="flex flex-wrap items-center gap-1.5"
                data-testid="change-source-destination-suggestions"
              >
                <span className="text-10 text-ink-3">Known sources</span>
                {destinationOptions.map((option) => (
                  <Button
                    key={option}
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 max-w-full truncate px-2 font-mono text-10"
                    title={option}
                    onClick={() => {
                      setDestinationUrl(option)
                      setOpcUsername('')
                      setOpcPassword('')
                      setReplaceSavedCredentials(false)
                      resetReview()
                    }}
                    disabled={busy}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            ) : summaryQuery.error ? (
              <p className="m-0 text-11 text-st-down">
                Known sources unavailable: {summaryQuery.error}
              </p>
            ) : null}
          </div>

          {toSubscribe && savedCredentialsAvailable ? (
            <div
              className="flex items-center gap-2 rounded-md border border-st-ok/25 bg-st-ok-bg px-3 py-2 text-11 text-st-ok"
              data-testid="change-source-saved-credentials"
            >
              <KeyRound className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                Saved OPC credentials will be reused automatically for this endpoint.
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 shrink-0 px-2 text-10"
                onClick={() => {
                  setReplaceSavedCredentials((current) => !current)
                  setOpcUsername('')
                  setOpcPassword('')
                  resetReview()
                }}
                disabled={busy}
              >
                {replaceSavedCredentials ? 'Use saved' : 'Replace'}
              </Button>
            </div>
          ) : null}

          {toSubscribe && !reuseSavedCredentials ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="change-source-opc-username" className="text-11">
                  OPC username (optional)
                </Label>
                <Input
                  id="change-source-opc-username"
                  value={opcUsername}
                  onChange={(event) => {
                    setOpcUsername(event.target.value)
                    resetReview()
                  }}
                  autoComplete="off"
                  className="h-8 text-11"
                  disabled={busy}
                  data-testid="change-source-opc-username"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="change-source-opc-password" className="text-11">
                  OPC password
                </Label>
                <Input
                  id="change-source-opc-password"
                  type="password"
                  value={opcPassword}
                  onChange={(event) => {
                    setOpcPassword(event.target.value)
                    resetReview()
                  }}
                  autoComplete="new-password"
                  className="h-8 text-11"
                  disabled={busy || !opcUsername.trim()}
                  data-testid="change-source-opc-password"
                />
              </div>
            </div>
          ) : null}

          {reviewed ? (
            <section
              className="rounded-md border border-line bg-bg-sunk/40 p-3"
              data-testid={result ? 'change-source-result' : 'change-source-preview'}
            >
              <div className="grid grid-cols-3 gap-2 text-12">
                <div>
                  <span className="text-ink-3">Converted</span>{' '}
                  <strong>{reviewed.converted.toLocaleString()}</strong>
                </div>
                <div>
                  <span className="text-ink-3">Skipped</span>{' '}
                  <strong>{reviewed.skipped.toLocaleString()}</strong>
                </div>
                <div>
                  <span className="text-ink-3">Failed</span>{' '}
                  <strong>{reviewed.failed.length.toLocaleString()}</strong>
                </div>
              </div>

              {reviewed.skippedDetails.length > 0 ? (
                <div className="mt-2 max-h-40 overflow-auto border-t border-line pt-1">
                  {reviewed.skippedDetails.map((item, index) => (
                    <div
                      key={`${item.tagId}-${item.nodeId ?? ''}-${index}`}
                      className="border-b border-dashed border-line py-1.5 last:border-0"
                      data-testid="change-source-skipped-item"
                    >
                      <div className="truncate font-mono text-11 text-ink">
                        {resultItemIdentity(item)}
                      </div>
                      <div className="text-10 leading-snug text-ink-3">{skipReason(item)}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {reviewed.failed.length > 0 ? (
                <div className="mt-2 max-h-32 overflow-auto border-t border-line pt-1">
                  {reviewed.failed.map((item, index) => (
                    <div
                      key={`${item.tagId}-${index}`}
                      className="border-b border-dashed border-line py-1.5 last:border-0"
                      data-testid="change-source-failed-item"
                    >
                      <div className="truncate font-mono text-11 text-ink">
                        {resultItemIdentity(item)}
                      </div>
                      <div className="text-10 leading-snug text-st-down">{item.reason}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {error ? (
            <div
              className="rounded-md bg-st-down-bg p-2 text-12 text-st-down"
              data-testid="change-source-error"
            >
              {error}
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-line px-5 py-3">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result ? (
            preview ? (
              <Button
                size="sm"
                onClick={() => void request(false)}
                disabled={busy || preview.converted === 0}
                data-testid="change-source-apply"
              >
                {busy ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    Applying…
                  </>
                ) : (
                  `Change ${preview.converted.toLocaleString()} tag${
                    preview.converted === 1 ? '' : 's'
                  }`
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => void request(true)}
                disabled={busy || tagIds.length === 0 || targetMode === null}
                data-testid="change-source-preview-action"
              >
                {busy ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    Previewing…
                  </>
                ) : (
                  'Preview changes'
                )}
              </Button>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
